/**
 * My FoodList Page - Personal saved items with tabs
 * Tabs: All | Restaurants | Recipes | Deals
 */
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Bookmark, ChefHat, UtensilsCrossed, Tag, Star, MapPin, Clock,
  Trash2, ExternalLink, Search, Filter, Grid, List, Globe, Lock
} from 'lucide-react';
import { useFoodlist } from '../contexts/FoodlistContext';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyFoodListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [viewMode, setViewMode] = useState('grid'); // grid | list
  const [searchQuery, setSearchQuery] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  const { user, loading: authLoading } = useAuth();
  const { items, loading, removeFromFoodlist, refreshFoodlist } = useFoodlist();
  const [isPublic, setIsPublic] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(false);

  useEffect(() => {
    if (user) {
      refreshFoodlist();
      // Fetch privacy setting
      fetch(`${API_URL}/api/foodlist/privacy`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setIsPublic(d.foodlist_public); })
        .catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab]);

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'all', label: 'All', icon: Bookmark, count: items?.length || 0 },
    { id: 'restaurants', label: 'Restaurants', icon: UtensilsCrossed, count: items?.filter(i => !i.is_recipe && i.source_mode !== 'deal')?.length || 0 },
    { id: 'recipes', label: 'Recipes', icon: ChefHat, count: items?.filter(i => i.is_recipe)?.length || 0 },
    { id: 'deals', label: 'Deals', icon: Tag, count: items?.filter(i => i.source_mode === 'deal')?.length || 0 },
  ];

  // Filter items based on active tab and search
  const filteredItems = (items || []).filter(item => {
    // Tab filter
    if (activeTab === 'restaurants' && (item.is_recipe || item.source_mode === 'deal')) return false;
    if (activeTab === 'recipes' && !item.is_recipe) return false;
    if (activeTab === 'deals' && item.source_mode !== 'deal') return false;
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        item.name?.toLowerCase().includes(q) ||
        item.restaurant?.toLowerCase().includes(q) ||
        item.cuisine?.toLowerCase().includes(q) ||
        item.location?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleRemove = async (itemId) => {
    await removeFromFoodlist(itemId);
  };

  const togglePrivacy = async () => {
    setPrivacyLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/foodlist/privacy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_public: !isPublic }),
      });
      if (res.ok) {
        const data = await res.json();
        setIsPublic(data.foodlist_public);
      }
    } catch {} finally { setPrivacyLoading(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-6">
            <Bookmark className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">My FoodList</h1>
          <p className="text-gray-500 mb-8">Sign in to save your favorite dishes, restaurants, and recipes</p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="px-8 py-3 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white font-semibold rounded-full hover:opacity-90 transition-opacity"
          >
            Sign In to Continue
          </button>
          {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">My FoodList</h1>
            <p className="text-gray-500">Your saved dishes, restaurants, and recipes</p>
          </div>
          <button
            onClick={togglePrivacy}
            disabled={privacyLoading}
            data-testid="foodlist-privacy-toggle"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border ${
              isPublic
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
            }`}
          >
            {privacyLoading ? (
              <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
            ) : isPublic ? (
              <Globe className="w-4 h-4" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            {isPublic ? 'Public' : 'Private'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 bg-white rounded-2xl p-1.5 shadow-sm border border-gray-100 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#FF6B35] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-white/20' : 'bg-gray-100'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search & View Toggle */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your saved items..."
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
            />
          </div>
          <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-200 p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading your FoodList...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {activeTab === 'recipes' ? (
                <ChefHat className="w-8 h-8 text-gray-400" />
              ) : activeTab === 'deals' ? (
                <Tag className="w-8 h-8 text-gray-400" />
              ) : (
                <Bookmark className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery ? 'No results found' : `No ${activeTab === 'all' ? 'items' : activeTab} saved yet`}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery 
                ? 'Try a different search term'
                : 'Start exploring and save items you love!'
              }
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#FF6B35] text-white font-medium rounded-full hover:bg-[#E85D04] transition-colors"
            >
              Explore Food
            </Link>
          </div>
        )}

        {/* Items Grid/List */}
        {!loading && filteredItems.length > 0 && (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'
            : 'space-y-4'
          }>
            {filteredItems.map((item) => (
              <FoodListCard 
                key={item.item_id} 
                item={item} 
                viewMode={viewMode}
                onRemove={() => handleRemove(item.item_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── FoodList Card Component ─────────────────────────────────────────── */
const FoodListCard = ({ item, viewMode, onRemove }) => {
  const fallbackImage = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=75';

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4 hover:shadow-md transition-shadow">
        <img 
          src={item.image || fallbackImage} 
          alt={item.name}
          className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
          onError={(e) => { e.target.src = fallbackImage; }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold text-gray-900 truncate">{item.name}</h3>
              {item.restaurant && (
                <p className="text-sm text-gray-500 truncate">{item.restaurant}</p>
              )}
            </div>
            {item.is_recipe && (
              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-full flex-shrink-0">
                Recipe
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
            {item.cuisine && <span>{item.cuisine}</span>}
            {item.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {item.location.split(',')[0]}
              </span>
            )}
            {item.rating > 0 && (
              <span className="flex items-center gap-1">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                {item.rating}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onRemove}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow group">
      <div className="relative aspect-[4/3]">
        <img 
          src={item.image || fallbackImage} 
          alt={item.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = fallbackImage; }}
        />
        {/* Overlay badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {item.is_recipe && (
            <span className="px-2 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
              <ChefHat className="w-3 h-3" />
              Recipe
            </span>
          )}
          {item.source_mode === 'deal' && (
            <span className="px-2 py-1 bg-purple-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
              <Tag className="w-3 h-3" />
              Deal
            </span>
          )}
        </div>
        {/* Remove button */}
        <button
          onClick={onRemove}
          className="absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-white opacity-0 group-hover:opacity-100 transition-all"
          title="Remove"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        {/* Rating badge */}
        {item.rating > 0 && (
          <div className="absolute bottom-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-full flex items-center gap-1">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            {item.rating}
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 line-clamp-1 mb-1">{item.name}</h3>
        {item.restaurant && (
          <p className="text-sm text-gray-500 line-clamp-1 mb-2">{item.restaurant}</p>
        )}
        <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
          {item.cuisine && (
            <span className="px-2 py-0.5 bg-gray-100 rounded-full">{item.cuisine}</span>
          )}
          {item.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {item.location.split(',')[0]}
            </span>
          )}
        </div>
        {item.flavorCaption && (
          <p className="text-xs text-gray-500 mt-2 line-clamp-2">{item.flavorCaption}</p>
        )}
      </div>
    </div>
  );
};

export default MyFoodListPage;
